/**
 * Shared PostHog wrapper for the browser surfaces (extension side panel, web
 * page). The SDK is an implementation detail behind this module — call sites
 * use `capture(eventName, props)` with registry names only; every payload goes
 * through `buildEventPayload` (allowlist + forbidden-key strip + event_class
 * stamp) before it can reach the SDK.
 *
 * Privacy contract (docs/analytics/event-spec.md §0):
 * - Opt-out is checked BEFORE the SDK is constructed, not after.
 * - Fail open, silently: no analytics error may ever affect app behavior.
 * - Location: events sent from a user's device are geo-enriched by PostHog at ingest
 *   (country/region/city/coordinates/timezone). The raw IP is NOT stored — project-level
 *   anonymize_ips discards it once the lookup is done. Enrichment is all-or-nothing;
 *   there is no country-only mode, which is why the full set lands. Disclosed in the
 *   privacy policy §13.2.
 */
import PostHog from "posthog-js-lite";
import {
  type AnalyticsEventName,
  buildEventPayload,
  POSTHOG_HOST,
  POSTHOG_PROJECT_KEY,
} from "./generated";

/**
 * Re-exported because this module's own public `capture` signature is typed in
 * terms of it — a consumer of the `@reflecto/client-core/analytics` subpath
 * cannot otherwise name the argument type it is required to pass.
 */
export type { AnalyticsEventName } from "./generated";

export interface AnalyticsInit {
  /**
   * The PC-class members of `SurfaceKind` (`@reflecto/core`), minus `phone`.
   * `web` is this union's name for `SurfaceKind`'s `pwa`.
   *
   * `cli` and `desktop` are listed so this union stops being narrower than the
   * type it shadows — NOT because either ships an analytics client today.
   * `apps/cli` deliberately has no analytics SDK, and the desktop app's client
   * arrives with `apps/desktop`. Widening here is a type-level correction;
   * emitting from a new surface additionally requires appending that surface to
   * the per-event `surfaces` array in `packages/core/analytics/events.json`,
   * or the dashboards under-count in silence.
   */
  surface: "extension" | "web" | "cli" | "desktop";
  appVersion: string;
  /** Read the persisted opt-out BEFORE init; opted-out users never construct the SDK. */
  getOptedOut: () => Promise<boolean>;
  /** Anonymous device UUID used as distinct_id (undefined → events stay anonymous). */
  getDistinctId: () => Promise<string | undefined>;
  /**
   * Whether this machine is one of our own dogfood devices, whose events PostHog's project-level
   * test-account filter excludes. Optional: a host that never sets the flag simply never stamps.
   */
  getInternalTester?: () => Promise<boolean>;
}

export interface ReflectoAnalytics {
  capture(event: AnalyticsEventName, props?: Record<string, unknown>): void;
  /** Merge this device's person with the paired phone's at pairing_completed. */
  aliasToPhone(androidDeviceId: string): void;
  /** Persisting the flag is the caller's job; this halts/resumes capture immediately. */
  setOptedOut(optedOut: boolean): void;
  /**
   * Flags (or unflags) this machine as one of our own test devices, applying to the very next
   * `capture`. Persisting the flag is the caller's job, as with `setOptedOut`.
   */
  setInternalTester(internalTester: boolean): void;
  /** Feature-flag seam for the future Pro rollout. Stub: always the default. */
  isFlagEnabled(key: string, defaultValue: boolean): boolean;
}

export function createAnalytics(init: AnalyticsInit): ReflectoAnalytics {
  let sdk: PostHog | null = null;
  let halted = false;
  // Read once during init below, then owned by setInternalTester. Kept out of the SDK's own
  // property machinery so it is stamped per event, exactly like surface/app_version.
  let internalTester = false;
  // An explicit setInternalTester beats the init-time read even if it lands first: the read is
  // async, so a host that toggles the flag during a slow storage read would otherwise see its
  // change silently reverted a tick later.
  let internalTesterExplicit = false;

  const ensureSdk = async () => {
    if (sdk) return;
    sdk = new PostHog(POSTHOG_PROJECT_KEY, {
      host: POSTHOG_HOST,
      flushAt: 5,
      flushInterval: 3000,
      persistence: "memory",
    });
    const distinctId = await init.getDistinctId();
    if (distinctId) sdk.identify(distinctId);
  };

  // Single lazy init chain; every operation queues behind it so ordering is
  // preserved and nothing runs before the opt-out check resolves.
  let chain: Promise<void> = (async () => {
    if (await init.getOptedOut()) {
      halted = true;
      return;
    }
    // Resolved before the first capture can run, so the very first event of a session on a
    // flagged machine is already stamped rather than leaking in unflagged.
    const stored = (await init.getInternalTester?.()) ?? false;
    if (!internalTesterExplicit) internalTester = stored;
    await ensureSdk();
  })().catch(() => {
    halted = true;
  });

  // Halt gates at ENQUEUE time only — a run-time re-check would drop the final
  // opt-out toggle event accepted just before a same-tick setOptedOut(true).
  const enqueue = (op: () => void, opts: { evenIfHalted?: boolean } = {}) => {
    if (halted && !opts.evenIfHalted) return;
    chain = chain
      .then(() => {
        if (sdk) op();
      })
      .catch(() => {});
  };

  return {
    capture(event, props = {}) {
      try {
        const payload = buildEventPayload(event, props);
        enqueue(() => {
          sdk?.capture(payload.name, {
            ...payload.properties,
            surface: init.surface,
            app_version: init.appVersion,
            // Spread-last and conditional: a call site cannot forge the flag, and an unflagged
            // machine sends no key at all (the PostHog rule is `is_not true`, which also matches
            // "not set", so absence is what leaves real users unaffected).
            ...(internalTester ? { is_internal_tester: true } : {}),
          });
        });
      } catch {
        // fail open
      }
    },
    aliasToPhone(androidDeviceId) {
      enqueue(() => sdk?.alias(androidDeviceId));
    },
    setOptedOut(optedOut) {
      if (optedOut) {
        // Flush the already-queued opt-out toggle event, then halt.
        enqueue(() => sdk?.flush().catch(() => {}), { evenIfHalted: true });
        halted = true;
      } else {
        halted = false;
        // If the user was opted out at startup the SDK was never constructed.
        chain = chain.then(() => ensureSdk()).catch(() => {});
      }
    },
    setInternalTester(next) {
      internalTesterExplicit = true;
      internalTester = next;
    },
    isFlagEnabled(_key, defaultValue) {
      return defaultValue;
    },
  };
}
